import { API_BASE_URL, REQUEST_TIMEOUT } from '../constants';
import { MarketingBanner, MarketingBannerType } from '../types';
import { authService } from './auth.service';

interface CreateMarketingBannerPayload {
  type: MarketingBannerType;
  link?: string | null;
  image: File;
}

class MarketingService {
  async getAll(): Promise<MarketingBanner[]> {
    const response = await authService.makeAuthenticatedRequest('/marketing');
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || 'Erro ao carregar banners de marketing');
    }

    return Array.isArray(data) ? data : [];
  }

  async create(payload: CreateMarketingBannerPayload): Promise<MarketingBanner> {
    const token = authService.getToken();
    if (!token) {
      authService.logout();
      throw new Error('Sessão expirada. Redirecionando para login...');
    }

    const formData = new FormData();
    formData.append('type', payload.type);
    if (payload.link) {
      formData.append('link', payload.link);
    }
    formData.append('image', payload.image);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}/marketing`, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      const data = await response.json().catch(() => null);

      if (response.status === 401) {
        authService.logout();
        throw new Error('Sessão expirada. Redirecionando para login...');
      }

      if (!response.ok) {
        throw new Error(data?.message || 'Erro ao criar banner de marketing');
      }

      return data as MarketingBanner;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        throw new Error('Tempo limite da requisição excedido');
      }
      if (error.message === 'Failed to fetch') {
        throw new Error('Erro de conexão. Verifique sua internet e tente novamente.');
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async remove(id: number): Promise<void> {
    const response = await authService.makeAuthenticatedRequest(`/marketing/${id}`, {
      method: 'DELETE',
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.message || 'Erro ao remover banner de marketing');
    }
  }
}

export const marketingService = new MarketingService();
